import type {
  BannerCreative,
  BannerRule,
  BannerSettingsEx,
  BannerTargeting,
  DisplayOptions,
  InventoryEntry,
} from '../../server/libs/siteSettings';
import { AntaaAccountSubType } from './antaaAccountSubType';
import { AntaaAccountType } from './antaaAccountType';
import { ArticleAuthor } from './article';

/**
 * サーバー側ルール評価への入力。
 */
export type ResolveBannerInventoryParams = {
  articleId: string;
  /** タグ名配列（DB型ではなくstring[]に揃える） */
  tags: string[];
  /** 診療科名配列 */
  departmentNames: string[];
  isVerifiedDoctor: boolean;
  isMobile: boolean;
};

/**
 * サーバーで決定済みの、クライアントに渡すバナー情報。
 * - inventories は fillers補完・按分まで完了済み
 * - creatives は使用対象 + isMobile に対応する画像を持つもののみ
 * - initialCreativeId はサーバー側で抽選した初回表示クリエイティブ
 */
export type ResolvedBannerInventory = {
  displayOptions: DisplayOptions;
  inventories: InventoryEntry[];
  creatives: Record<string, BannerCreative>;
  initialCreativeId: string;
};

// ============================================================
// 内部ユーティリティ
// ============================================================

/**
 * creative が有効かどうかを判定する。
 * - name: 非空文字列
 * - alt: 非空文字列
 * - linkUrl: 非空文字列
 * - images: オブジェクト、且つ pc または mobile のいずれかが有効な imgS3Key を持つ
 */
function isValidCreative(entry: BannerCreative): boolean {
  // 必須フィールドのチェック
  if (typeof entry.name !== 'string' || entry.name.length === 0) {
    return false;
  }
  if (typeof entry.alt !== 'string' || entry.alt.length === 0) {
    return false;
  }
  if (typeof entry.linkUrl !== 'string' || entry.linkUrl.length === 0) {
    return false;
  }

  // images オブジェクトの存在チェック
  if (!entry.images || typeof entry.images !== 'object') {
    return false;
  }

  // pc または mobile のいずれかに有効な imgS3Key が存在するかチェック
  const pcHasValidKey =
    entry.images.pc &&
    typeof entry.images.pc.imgS3Key === 'string' &&
    entry.images.pc.imgS3Key.length > 0;
  const mobileHasValidKey =
    entry.images.mobile &&
    typeof entry.images.mobile.imgS3Key === 'string' &&
    entry.images.mobile.imgS3Key.length > 0;

  return pcHasValidKey || mobileHasValidKey;
}

/**
 * InventoryEntry が有効かどうかを判定する。
 * - creativeId: 非空文字列
 * - weight: 正の有限数（NaN・Infinity・0以下は不正）
 */
function isValidInventoryEntry(entry: InventoryEntry): boolean {
  return (
    typeof entry.creativeId === 'string' &&
    entry.creativeId.length > 0 &&
    Number.isFinite(entry.weight) &&
    entry.weight > 0
  );
}

/**
 * BannerRule が有効かどうかを判定する。
 * - id: 非空文字列
 * - priority: 有限数
 * - targeting: 存在する
 * - inventories: 配列
 */
function isValidRule(rule: BannerRule): boolean {
  return (
    typeof rule.id === 'string' &&
    rule.id.length > 0 &&
    Number.isFinite(rule.priority) &&
    rule.targeting !== null &&
    rule.targeting !== undefined &&
    Array.isArray(rule.inventories)
  );
}

/**
 * スライド投稿者が法人（製薬）または法人（出版社）アカウントかどうかを判定する。
 * これらのアカウントが投稿したスライドでは、articleId 指定以外のバナー表示と、
 * フィラーによる補填を抑止する。
 */
function isBannerRestrictedAuthor(author: ArticleAuthor): boolean {
  return (
    author.antaaAccountType === AntaaAccountType.CORPORATION &&
    (author.antaaAccountSubType === AntaaAccountSubType.PHARMACEUTICAL ||
      author.antaaAccountSubType === AntaaAccountSubType.PUBLISHER)
  );
}

/**
 * ターゲティング条件がスライドに一致するかを判定する。
 * excludeArticleIds の判定はターゲティング種別共通で先に評価する。
 */
function matchesTargeting(
  params: ResolveBannerInventoryParams,
  targeting: BannerTargeting,
  author: ArticleAuthor,
): boolean {
  const { articleId, tags, departmentNames } = params;

  if (targeting.excludeArticleIds?.includes(articleId)) {
    return false;
  }

  // スライド投稿者が法人（製薬）または法人（出版社）アカウント、かつ
  // ターゲティング種別がarticleId以外の場合は、バナーの表示を抑止する
  if (isBannerRestrictedAuthor(author) && targeting.type !== 'articleId') {
    return false;
  }

  switch (targeting.type) {
    case 'articleId':
      return targeting.values.includes(articleId);
    case 'tag':
      return targeting.values.some((tagName) => tags.includes(tagName));
    case 'andTags':
      // いずれか1セット内のタグがすべて含まれていればマッチ
      return targeting.values.some((tagSet) =>
        tagSet.every((tagName) => tags.includes(tagName)),
      );
    case 'department':
      return targeting.values.some((dept) => departmentNames.includes(dept));
    case 'all':
      return true;
    default:
      return false;
  }
}

/**
 * fillers の各エントリの weight を、remainingWeight を比率配分した値に変換する。
 * 比率配分: effectiveFillerWeight_i = remainingWeight * (filler.weight_i / sum(fillers.weight))
 */
function buildFillerPool(
  fillers: InventoryEntry[],
  remainingWeight: number,
): InventoryEntry[] {
  if (fillers.length === 0 || remainingWeight <= 0) return [];
  const totalFillerWeight = fillers.reduce((sum, e) => sum + e.weight, 0);
  if (totalFillerWeight <= 0) return [];

  return fillers.map((entry) => ({
    creativeId: entry.creativeId,
    weight: (remainingWeight * entry.weight) / totalFillerWeight,
  }));
}

/** weight を合計100になるよう正規化する。 */
function normalizeWeightsTo100(entries: InventoryEntry[]): InventoryEntry[] {
  if (entries.length === 0) return [];
  const total = entries.reduce((sum, e) => sum + e.weight, 0);
  if (total <= 0) return [];
  return entries.map((entry) => ({
    creativeId: entry.creativeId,
    weight: (entry.weight * 100) / total,
  }));
}

/**
 * inventories を、creatives 存在 & isMobile に対応する画像存在でフィルタする。
 */
function filterByAvailableImages(
  inventories: InventoryEntry[],
  creatives: Record<string, BannerCreative>,
  isMobile: boolean,
): {
  filteredInventories: InventoryEntry[];
  filteredCreatives: Record<string, BannerCreative>;
} {
  const filteredInventories: InventoryEntry[] = [];
  const filteredCreatives: Record<string, BannerCreative> = {};

  for (const entry of inventories) {
    const creative = creatives[entry.creativeId];
    if (!creative) continue;

    const requiredKey = isMobile
      ? creative.images?.mobile?.imgS3Key
      : creative.images?.pc?.imgS3Key;
    if (!requiredKey) continue;

    filteredInventories.push(entry);
    filteredCreatives[entry.creativeId] = creative;
  }

  return { filteredInventories, filteredCreatives };
}

// ============================================================
// 公開API
// ============================================================

/**
 * 重み付き抽選。entriesが空 / 全weightが0以下の場合は null を返す。
 */
export function selectByWeight(entries: InventoryEntry[]): string | null {
  if (entries.length === 0) return null;
  if (entries.length === 1) {
    return entries[0].weight > 0 ? entries[0].creativeId : null;
  }
  const totalWeight = entries.reduce((sum, e) => sum + e.weight, 0);
  if (totalWeight <= 0) return null;
  let random = Math.random() * totalWeight;
  for (const entry of entries) {
    random -= entry.weight;
    if (random <= 0) return entry.creativeId;
  }
  return entries[entries.length - 1].creativeId;
}

/**
 * サーバー側で BannerSettingsEx を評価し、クライアントに渡す
 * ResolvedBannerInventory を生成する。
 *
 * @param args.params - バナー表示対象のスライド情報と閲覧者情報。
 *   記事ID、タグ、診療科、認証済み医師かどうか、モバイル表示かどうかを含む。
 * @param args.settings - バナー設定。
 *   表示オプション、クリエイティブ、ターゲティングルール、フィラーを含む。
 * @param args.author - スライド投稿者の情報。
 *   投稿者のアカウントタイプ／アカウントサブタイプを含む。
 *   法人（製薬）／法人（出版社）アカウントの場合は、articleId 指定以外の
 *   ターゲティングと、フィラーによる補填（空き枠・一致rule無しのいずれも）を抑止する。
 * @param args.onInvalid - 不正な rule または filler を検出したときに呼ばれるコールバック。
 *   呼び出し元でログ出力等に使用する。
 * @returns 解決済みのバナー情報。
 *   表示可能な設情報がない、必須設定が不足している、または初回表示クリエイティブを
 *   決定できない場合は `null`。
 */

export type ResolveBannerInventoryArgs = {
  params: ResolveBannerInventoryParams;
  settings: BannerSettingsEx;
  author: ArticleAuthor;
  onInvalid?: (msg: string) => void;
};

export function resolveBannerInventory({
  params,
  settings,
  author,
  onInvalid,
}: ResolveBannerInventoryArgs): ResolvedBannerInventory | null {
  const { isVerifiedDoctor, isMobile } = params;
  const { displayOptions, creatives = {}, rules = [], fillers = [] } = settings;

  // S3 から読み込んだ JSON に必須フィールドが欠落している場合は安全に null を返す
  if (!displayOptions) return null;

  // creatives の不正エントリを除外する
  const validCreatives: Record<string, BannerCreative> = {};
  for (const [creativeId, creative] of Object.entries(creatives)) {
    if (isValidCreative(creative)) {
      validCreatives[creativeId] = creative;
    } else {
      onInvalid?.(
        `bannerSettingsEx の creatives に不正な値があります; creativeId=${creativeId}`,
      );
    }
  }

  // fillers の不正エントリを除外する
  const validFillers = fillers.filter((entry) => {
    if (isValidInventoryEntry(entry)) return true;
    onInvalid?.(
      `bannerSettingsEx の fillers に不正な値があります; creativeId=${
        entry.creativeId ?? 'unknown'
      }; weight=${entry.weight}`,
    );
    return false;
  });

  // 1. priority 降順 + 同値時は配列順を維持して評価する
  // Array.sort は安定ソートのため、配列順がそのまま同値時の順序になる
  const sortedRules = [...rules].sort((a, b) => b.priority - a.priority);

  let selectedInventories: InventoryEntry[] | null = null;
  let selectedCreatives: Record<string, BannerCreative> = {};

  for (const rule of sortedRules) {
    // 2. 不正な rule はスキップする
    if (!isValidRule(rule)) {
      onInvalid?.(
        `bannerSettingsEx の rules に不正な値があります; ruleId=${
          rule.id ?? 'unknown'
        }`,
      );
      continue;
    }

    // 3. 認証済み医師のみ表示の制限チェック
    if (rule.visibility?.isVerifiedDoctorOnly && !isVerifiedDoctor) {
      continue;
    }
    // 4. ターゲティング判定
    if (!matchesTargeting(params, rule.targeting, author)) continue;

    // 5. inventory weight合計で分岐
    const totalInventoryWeight = rule.inventories.reduce(
      (sum, e) => sum + e.weight,
      0,
    );

    // weight合計が100未満の場合は空き枠をフィラーで補填する。
    // ただし投稿者が法人（製薬）または法人（出版社）アカウントの場合は、
    // 自社バナー以外を表示しないため、フィラーでは補填せず
    // rule の inventories のみを weight 合計100に正規化して使用する。
    let candidateInventories: InventoryEntry[];
    if (totalInventoryWeight >= 100) {
      candidateInventories = rule.inventories;
    } else if (isBannerRestrictedAuthor(author)) {
      candidateInventories = normalizeWeightsTo100(rule.inventories);
    } else {
      candidateInventories = [
        ...rule.inventories,
        ...buildFillerPool(validFillers, 100 - totalInventoryWeight),
      ];
    }

    // 6. creatives 存在 & isMobile に対応する画像存在でフィルタ
    // 有効な inventories が残った場合のみこの rule を採用する
    const { filteredInventories, filteredCreatives } = filterByAvailableImages(
      candidateInventories,
      validCreatives,
      isMobile,
    );

    if (filteredInventories.length > 0) {
      selectedInventories = filteredInventories;
      selectedCreatives = filteredCreatives;
      break;
    }
    // filtered が空なら次の rule を評価する
  }

  // 7. 一致 rule なし（または全 rule が画像フィルタで除外）→ fillers のみ採用
  if (selectedInventories === null) {
    // スライド投稿者が法人（製薬）または法人（出版社）アカウントの場合は、フィラーで補填しない
    if (isBannerRestrictedAuthor(author)) {
      return null;
    }

    // フィラーで補填
    if (validFillers.length === 0) return null;
    const { filteredInventories, filteredCreatives } = filterByAvailableImages(
      normalizeWeightsTo100(validFillers),
      validCreatives,
      isMobile,
    );
    if (filteredInventories.length === 0) return null;
    selectedInventories = filteredInventories;
    selectedCreatives = filteredCreatives;
  }

  // 初回表示クリエイティブをサーバー側で決定
  const initialCreativeId = selectByWeight(selectedInventories);
  if (!initialCreativeId || !selectedCreatives[initialCreativeId]) return null;

  return {
    displayOptions,
    inventories: selectedInventories,
    creatives: selectedCreatives,
    initialCreativeId,
  };
}
